<template>
  <div class="posts-widget">
    <div v-if="loading" class="posts-loading">Loading popular posts...</div>
    <div v-else class="list-container">
      <div v-if="error" class="posts-error">{{ error }}</div>
      <ul v-else class="posts-list">
        <li v-for="post in popularPosts" :key="post.id" class="post-item">
          <router-link
            :to="`/${post.type}/${post.slug}`"
            class="post-link"
          >
            <div class="post-content">
              <div class="post-left">
                <div class="post-icon">
                  <i :class="getPostTypeIcon(post.type)"></i>
                </div>
                <div class="post-text">
                  <h3 class="post-title" v-if="post.title && post.title.rendered">
                    {{ post.title.rendered }}
                  </h3>
                  <div class="post-meta">
                    <span v-if="post._embedded && post._embedded.author && post._embedded.author[0]">
                      By {{ post._embedded.author[0].name }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="post-type-badge">
                {{ getPostTypeName(post.type) }}
              </div>
              <div class="post-right">
                <span class="view-button">View <i class="fas fa-chevron-right" style="margin-bottom: 1px; font-size: 75%;"></i></span>
              </div>
            </div>
          </router-link>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { fetchPosts, fetchCurrentUser, fetchCategoriesBySlugs, category_from_role } from '@/utils/fetch.js';

export default {
  data() {
    return {
      loading: true,
      error: null,
      allPosts: [],
      popularPosts: [],
      postTypes: ['post', 'announcement', 'process', 'training'],
      userCategoryIds: [],
    };
  },
  methods: {
    async loadPopularPosts() {
      this.loading = true;
      this.error = null;
      try {
        // Get user and allowed category IDs
        const user = await fetchCurrentUser();
        const roles = user.my_roles || [];
        const roleCategorySlugs = roles.map(role => category_from_role[role]).filter(Boolean);

        // Always include 'general'
        const allSlugs = [...roleCategorySlugs, 'general'];
        const categories = allSlugs.length ? await fetchCategoriesBySlugs(allSlugs) : [];
        const allowedCategories = categories.map(cat => cat.id);

        this.userCategoryIds = allowedCategories;

        // Fetch posts
        const posts = await fetchPosts('&per_page=10&_embed', this.postTypes);

        // Filter posts by allowed categories
        const filteredPosts = posts.filter(post =>
          post.categories.some(catId => this.userCategoryIds.includes(catId))
        );

        // Sort by views descending, take top 2
        this.popularPosts = filteredPosts
          .sort((a, b) => (b.views || 0) - (a.views || 0))
          .slice(0, 2);
        this.allPosts = filteredPosts;
      } catch (err) {
        this.error = `Failed to fetch popular posts: ${err.message}`;
      } finally {
        this.loading = false;
      }
    },
    getPostTypeIcon(type) {
      switch (type) {
        case 'post':
          return 'fa-light fa-file-alt';
        case 'announcement':
          return 'fa-light fa-megaphone';
        case 'process':
          return 'fa-light fa-clipboard-list-check';
        case 'training':
          return 'fa-light fa-screen-users';
        default:
          return 'fa-light fa-file-alt';
      }
    },
    getPostTypeName(type) {
      switch (type) {
        case 'post':
          return 'Post';
        case 'announcement':
          return 'Announcement';
        case 'process':
          return 'Process';
        case 'training':
          return 'Training';
        default:
          return 'Post';
      }
    }
  },
  mounted() {
    this.loadPopularPosts();
  }
};
</script>

<style scoped>
.posts-widget {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  width: 100%;
  text-align: center;
  flex: 1 1 0;
  min-height: 180px; /* Fixed height for 2 posts */
  max-height: 220px; /* Prevent growing too tall */
  box-sizing: border-box;
  overflow-x: hidden;
}

.posts-list {
  flex: 1 1 0;
  padding-right: 10px;
  padding-left: 0;
  margin: 0;
  list-style: none;
  min-height: 0;
}

.list-container {
  height: 100%;
  padding-right: 5px;
  overflow-y: hidden;
  overflow-x: hidden;
  --fade-amount: 1.5%;
  mask-image: linear-gradient(
    transparent,
    black var(--fade-amount), 
    black calc(100% - var(--fade-amount)), 
    transparent
  );
}

.post-item {
  padding-bottom: 0.5rem;
}

.post-item:last-child {
  border-bottom: none;
}

.posts-loading,
.posts-error {
  padding: 1rem 0;
  text-align: center;
  color: #888;
}

.post-content {
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  position: relative;
  padding: 0 120px 0 0;
  overflow: hidden;
}

.post-left {
  display: flex;
  align-items: center;
  max-width: 50%;
  min-width: 0;
}

.post-link {
  text-decoration: none;
  color: inherit;
  display: block;
  width: 100%;
  border-radius: 25px;
  padding: .5rem;
  transition: background-color 0.2s ease, transform 0.3s ease;
  box-sizing: border-box;
}

.post-link:hover {
  background-color: #F9F8F6F0;
  transform: translateX(5px);
}

.post-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #f0f0f0;
  margin-right: 1rem;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.post-icon i {
  color: #5e5d5b;
}

.post-text {
  flex: 1;
  text-align: left;
  min-width: 0;
  max-width: 100%;
}

.post-title {
  margin: 0 0 0.25rem;
  color: #5F5E5B;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.post-meta {
  font-size: 0.85em;
  color: #666;
}

.post-type-badge {
  padding: 0.35em 0;
  font-size: 0.85em;
  font-weight: 600;
  color: #94918B;
  margin: 0 auto;
  white-space: nowrap;
  width: 100px;
  text-align: center;
  position: absolute;
  left: 50%;
  transform: translateX(50%);
}

.post-right {
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
}

.view-button {
  display: inline-block;
  font-size: 0.85em;
  white-space: nowrap;
  padding-right: 1em;
  color: var(--primary-color);
}


@media (max-width: 1100px) {
  .posts-widget {
    min-height: 400px;
    flex: 1 1 auto;
  }
  .post-title {
    white-space: normal;
  }
  .post-content {
    padding-right: 70px;
  }
}

@media (max-width: 800px) {
  .post-date-circle {
    width: 40px;
    height: 40px;
    font-size: 0.9rem;
  }

  .post-left {
    max-width: 100%;
  }

  .post-type-badge {
    display: none;
  }

  .date-day {
    font-size: 1.1em;
  }

  .date-month {
    font-size: 0.75em;
  }

  .post-title {
    font-size: 1rem;
  }

  .post-meta {
    font-size: 0.75em;
  }

  .post-type-badge {
    width: 80px;
    font-size: 0.75em;
  }

  .view-button {
    font-size: 0.75em;
    padding-right: 0.75em;
  }
}
</style>